import { useState } from "react";
import Button from "../../../common/Button";
import KaModal from "../../../common/KaModal";
import useDisplayMessage from "../../../common/lib/hooks/useDisplayMessage";
import KaAlert from "../../../common/lib/KaAlert";
import { Bay, DispenseType } from "../../../requests/useUpdateBaySettings";

interface UpdateBayModalProps {
  bay: Bay,
  onUpdate: (dispenseType: DispenseType, bulkReceiving: boolean, temporarilyDisabled: boolean) => void,
  toggle: () => void,
}

const toBlendBulk = (dispenseType: DispenseType) => {
  switch (dispenseType) {
    case DispenseType.Blended:
      return { blend: true, bulk: false };

    case DispenseType.Bulk:
      return { blend: false, bulk: true };

    case DispenseType.Both:
      return { blend: true, bulk: true };

    case DispenseType.None:
      return { blend: false, bulk: false };
  }
}

export const UpdateBayModal = ({ bay, onUpdate, toggle }: UpdateBayModalProps) => {
  const displayMessage = useDisplayMessage();
  const [bulk, setBulk] = useState<boolean>(toBlendBulk(bay.dispenseType).bulk);
  const [blend, setBlend] = useState<boolean>(toBlendBulk(bay.dispenseType).blend);
  const [bulkReceiving, setBulkReceiving] = useState<boolean>(bay.bulkReceiving);
  const [temporarilyDisabled, setTemporarilyDisabled] = useState<boolean>(bay.temporarilyDisabled);

  const update = () => {
    let dispenseType: DispenseType;

    if (bulk && blend) {
      dispenseType = DispenseType.Both;
    } else if (bulk) {
      dispenseType = DispenseType.Bulk;
    } else if (blend) {
      dispenseType = DispenseType.Blended;
    } else {
      dispenseType = DispenseType.None;
    }

    onUpdate(dispenseType, bulkReceiving, temporarilyDisabled);
  };

  return <KaModal
    isOpen={true}
    toggle={toggle}
    title={"Bay Prioritization"}
    body={(<>
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          checked={bulk}
          onChange={() => setBulk(!bulk)}
          id="cbxBulk" />
        <label className="h5" htmlFor="cbxBulk">
          Bulk Loadout
        </label>
      </div>
      <div className="ms-4">
        <p>
          Bulk orders will be directed to "Bulk" bays that have all their products available.
          Bulk orders will never be assigned to "Blend" bays.
          If multiple bulk bays can handle the order, then the bay that was used the longest ago will be used.
        </p>
        <p>
          If no "Bulk" bay has the products needed, then the order will be assigned to a "Bulk & Blend" bay.
        </p>
      </div>
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          checked={blend}
          onChange={() => setBlend(!blend)}
          id="cbxBlend" />
        <label className="h5" htmlFor="cbxBlend">
          Blend Loadout
        </label>
      </div>
      <div className="ms-4">
        <p>
          Blend orders will be directed to any "Blend" or "Bulk & Blend" bay that has all the products on the order.
          If multiple bays can handle the order, then the bay that was used the longest ago will be used.
        </p>
      </div>
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          checked={bulkReceiving}
          onChange={() => setBulkReceiving(!bulkReceiving)}
          id="cbxBulkReceiving" />
        <label className="h5" htmlFor="cbxBulkReceiving">
          Bulk Receiving
        </label>
      </div>
      <div className="ms-4">
        <p>
          When a PO number is entered during check-in, and the driver proceeds, the system will prioritize bays designated exclusively for Bulk Receiving. 
          As a result, bays allocated for Bulk/Blend dispensing will not be considered for this prioritization and will remain unavailable for Bulk Receiving operations.
        </p>
      </div>
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          checked={temporarilyDisabled}
          onChange={() => setTemporarilyDisabled(!temporarilyDisabled)}
          id="cbxTemporarilyDisabled" />
        <label className="h5" htmlFor="cbxTemporarilyDisabled">
          Temporarily Disable Bay
        </label>
      </div>
      <div className="ms-4">
        <p>
          The Temporarily Disable Bay feature allows you to deactivate a bay without losing its settings. 
          While disabled, the bay won't process orders, but all configurations remain intact for easy reactivation when needed.
        </p>
      </div>
    </>)}
    footer={(<>
      <button type="button" className="btn btn-white" onClick={toggle}>
        Close
      </button>
      <Button onClick={update}>
        Update
      </Button>
    </>)}
  />
}